import { useBooleanFlagValue } from '@openfeature/react-sdk'
import {
  CarrierType,
  CarrierVisitDirection,
  OrderResponseDto,
  OrderStatus,
} from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { ContainerJourneyStatus } from '@planning/components/molecules/ContainerJourneyStatusChip'
import { useOrderNavigation } from '@planning/hooks/useOrderNavigation'
import { useVesselVisitTableMenuOptions } from '@planning/hooks/useVesselVisitTableMenuOptions'
import { IContainerJourneyItem } from '@planning/pages/Issues/Stores/ContainerJourneyItem'
import { IOrderItem } from '@planning/rt-stores/order/OrderItem'
import { adminActionService } from '@planning/services'
import { useTranslate } from '@tolgee/react'
import { ButtonOption, ButtonSize, OptionsButton } from '@tom-ui/ui'
import { authStore, PATHS } from '@tom-ui/utils'
import { computed } from 'mobx'
import { FC, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { useAddHoldDialog } from '../../Hooks/useAddHoldDialog'
import { useUpdateOrderStowagePosition } from '../../Hooks/useUpdateOrderStowagePosition'
import { AddHoldDialog } from './AddHoldDialog'
import { ContainerYardOperationViewStore } from '../OrderGrouping'

type Props = {
  order?: IOrderItem
  journey?: IContainerJourneyItem
  containerYardOperationViewStore: ContainerYardOperationViewStore
  extraOptions?: ButtonOption[]
  size?: ButtonSize
}

export const OrderListMenuOptions: FC<Props> = ({
  order,
  journey,
  containerYardOperationViewStore,
  extraOptions = [],
  size = 'medium',
}) => {
  const { t } = useTranslate()
  const navigate = useNavigate()

  const { openEditOrder, navigateCheckoutContainerPage, navigateCheckinContainerPage } =
    useOrderNavigation()

  const { appViewStore, issueItemStore } = usePlanningStore()

  const isContainerHistoryTurnedOn = useBooleanFlagValue('container-history', false)

  const { onOrderHistory } = useVesselVisitTableMenuOptions()
  const { onEditPostion } = useUpdateOrderStowagePosition()
  const { onAddHold, dialogProps } = useAddHoldDialog()

  const onDeleteOrder = useCallback(
    async (orders: IOrderItem[]) => {
      const count = orders.length
      const confirmationMessageKey =
        count === 1 ? 'orderWillBeDeletedContinue' : 'ordersWillBeDeletedContinue'
      const confirmationMessage =
        count === 1
          ? 'Order will be deleted. Continue?'
          : `The selected ${count} Orders will be deleted. Continue?`

      const hasServiceOrders = orders.some(item => item.data.hasServiceOrders)

      const isConfirmed = await appViewStore.setOpenConfirmDialog(
        true,
        t(confirmationMessageKey, confirmationMessage),
        t('confirmOrderDelete', 'Confirm order delete'),
        hasServiceOrders
          ? t(
              'anyAssociatedStrippingOrStuffingOrdersWillBeDeleted',
              'Any associated stripping/stuffing orders will be deleted',
            )
          : undefined,
      )

      if (!isConfirmed) return

      try {
        await adminActionService.deleteByIds(orders.map(item => item.id))
        appViewStore.setShowAlert(
          'success',
          t('successfullyDeletedOrder', 'Successfully deleted order'),
        )
      } catch (e) {
        appViewStore.setShowAlert('error', t('failedToDeleteOrder', 'Failed to delete order'))
      }
    },
    [appViewStore, t],
  )

  const onResolveOrderIssue = useCallback(
    async (selectedItemId: number) =>
      navigate(`${PATHS.issues.url}`, {
        state: { filterByRailCarTrackPositionId: selectedItemId },
      }),
    [navigate],
  )

  const orderIssuesTotal = computed<number>(() => {
    const orderData = order?.data

    const orderIssues = issueItemStore.issues.filter(
      i => orderData?.notResolvedIssueIds?.includes(i.id) ?? false,
    )

    return orderIssues.length
  }).get()

  const addCopyDetailAction = useCallback(
    (order: OrderResponseDto, actions: ButtonOption[]) => {
      if (order.containerNumber || order.referenceNumber) {
        const copyActions: ButtonOption[] = []

        if (order.containerNumber) {
          copyActions.push({
            label: t('containerNumber', 'Container Number'),
            onClick: () => {
              const containerNumber = order.containerNumber
              if (order) navigator.clipboard.writeText(containerNumber!)
            },
          })
        }

        if (order.referenceNumber) {
          copyActions.push({
            label: t('referenceNumber', 'Reference Number'),
            onClick: () => {
              const referenceNumber = order.referenceNumber
              navigator.clipboard.writeText(referenceNumber!)
            },
          })
        }

        actions.push({
          label: t('copyDetail', 'Copy detail'),
          children: copyActions,
        })
      }
    },
    [t],
  )

  const addResolveIssuesAction = useCallback(
    (order: OrderResponseDto, actions: ButtonOption[]) => {
      if (orderIssuesTotal > 0 && order.railcarTrackPositionId) {
        actions.push({
          label: t('resolve', 'Resolve'),
          onClick: () => onResolveOrderIssue(order.railcarTrackPositionId!),
        })
      }
    },
    [onResolveOrderIssue, orderIssuesTotal, t],
  )

  const addAdminActions = useCallback(
    (order: IOrderItem, actions: ButtonOption[]) => {
      let adminActions: ButtonOption[] = []
      const orderData = order.data

      const isAdmin = authStore.isAdmin()

      if (isAdmin) {
        adminActions = [
          {
            label: t('checkInContainer', 'Check in container'),
            disabled:
              (journey?.status !== ContainerJourneyStatus.Expected &&
                journey?.status !== ContainerJourneyStatus.Arrived) ||
              orderData.direction === CarrierVisitDirection.Outbound,
            onClick: () => {
              navigateCheckinContainerPage(orderData)
            },
          },
          {
            label: t('undoCheckIn', 'Undo check in'),
            disabled:
              journey?.status === ContainerJourneyStatus.Expected ||
              journey?.status === ContainerJourneyStatus.Arrived ||
              journey?.status === ContainerJourneyStatus.Departed ||
              orderData.direction === CarrierVisitDirection.Outbound,
            onClick: async () => {
              const confirmed = await appViewStore.setOpenConfirmDialog(
                true,
                t(
                  'containerWillBeResetToExpected',
                  'Container no. {container} will be reset to Expected status and any associated jobs will be terminated. This will afect billing.',
                  { container: orderData.containerNumber },
                ),
                t('undoCheckin', 'Undo check in'),
              )
              if (confirmed) {
                adminActionService.undoOrderCheckIn(orderData.id)
              }
            },
          },
          {
            label: t('checkOutContainer', 'Check out container'),
            disabled:
              journey?.status === ContainerJourneyStatus.Expected ||
              journey?.status === ContainerJourneyStatus.Arrived ||
              journey?.status === ContainerJourneyStatus.Departed ||
              orderData.direction === CarrierVisitDirection.Inbound ||
              (!journey && orderData.status === OrderStatus.Fulfilled),
            onClick: () => {
              navigateCheckoutContainerPage(orderData)
            },
          },
          {
            label: t('undoCheckOut', 'Undo check out'),
            disabled:
              (journey && journey.status !== ContainerJourneyStatus.Departed) ||
              orderData.direction === CarrierVisitDirection.Inbound ||
              (!journey && orderData.status === OrderStatus.Open),
            onClick: async () => {
              if (!orderData.containerNumber) {
                appViewStore.setShowAlert(
                  'error',
                  t('containerNumberIsMissing', 'Container number is missing'),
                )
              }

              containerYardOperationViewStore.open(
                orderData.containerNumber!,
                orderData.id,
                t('undoCheckout', 'Undo check out'),
                t(
                  'containerWillBeResetToTerminal',
                  'Container no. {container} will be reset to On terminal status. This will afect billing.',
                  { container: orderData.containerNumber },
                ),
                async () => await adminActionService.undoOrderCheckOut(orderData.id),
                journey?.data.id,
              )
            },
          },
          {
            label: t('correctYardLocation', 'Correct yard location'),
            disabled: journey?.status !== ContainerJourneyStatus.OnYard,
            onClick: () => {
              if (orderData.containerNumber) {
                containerYardOperationViewStore.open(
                  orderData.containerNumber,
                  orderData.id,
                  undefined,
                  undefined,
                  undefined,
                  journey?.data.id,
                )
              } else {
                appViewStore.setShowAlert(
                  'error',
                  t('containerNumberIsMissing', 'Container number is missing'),
                )
              }
            },
          },
        ]

        if (
          orderData.direction === CarrierVisitDirection.Outbound &&
          orderData.carrierVisitType === CarrierType.Vessel
        ) {
          const editPositionTitle = t('editActualPosition', 'Edit actual position')

          adminActions.push({
            label: editPositionTitle,
            disabled: orderData.status !== OrderStatus.Fulfilled,
            onClick: () => onEditPostion(editPositionTitle, orderData),
          })
        }

        adminActions.push({
          label: t('delete', 'Delete'),
          onClick: () => onDeleteOrder([order]),
        })
      }

      actions.push({
        label: t('admin', 'Admin'),
        children: adminActions,
      })
    },
    [
      appViewStore,
      journey,
      containerYardOperationViewStore,
      t,
      navigateCheckinContainerPage,
      navigateCheckoutContainerPage,
      onDeleteOrder,
      onEditPostion,
    ],
  )

  const actions: ButtonOption[] = useMemo(() => {
    const orderData = order?.data
    const container = order?.container
    const isDummyContainer = !container?.data.number?.length

    if (!orderData) {
      return []
    }

    const nonNumericOrderId = orderData.nonNumericOrderId
    if (isDummyContainer && nonNumericOrderId)
      return [
        {
          label: t('assignContainer', 'Assign container'),
          testId: 'order-assign-container-option',
          onClick: () => {
            if (orderData.carrierVisitType === CarrierType.Vessel) {
              const directionPath =
                orderData.direction === CarrierVisitDirection.Outbound
                  ? PATHS.visits.vesselVisits.load.url
                  : PATHS.visits.vesselVisits.discharge.url

              navigate(
                `${PATHS.visits.vesselVisits.url}/${orderData.carrierVisitId}/${directionPath}/nnr-orders/assign-container/${nonNumericOrderId}`,
              )
            } else {
              navigate(`${PATHS.orders.nonNumericReleases.url}/${nonNumericOrderId}/details`)
            }
          },
        },
      ]

    const actions: ButtonOption[] = [
      {
        label: t('viewDetails', 'View details'),
        testId: 'order-view-details-option',
        onClick: () => openEditOrder(orderData),
      },
      {
        label: t('addHold', 'Add hold'),
        disabled: journey?.status === ContainerJourneyStatus.Departed || !journey?.data.id,
        onClick: () => journey?.data.id && onAddHold(journey.data.id),
      },
    ]

    if (
      orderData.direction === CarrierVisitDirection.Outbound &&
      orderData.carrierVisitType === CarrierType.Vessel
    ) {
      const editPlannedPositionTitle = t('editPlannedPosition', 'Edit planned position')
      actions.push({
        label: editPlannedPositionTitle,
        disabled: orderData.status === OrderStatus.Fulfilled,
        onClick: () => onEditPostion(editPlannedPositionTitle, orderData),
      })
    }

    if (isContainerHistoryTurnedOn) {
      actions.push({
        label: t('history', 'History'),
        onClick: () => onOrderHistory(orderData.containerId!, orderData.carrierVisitId),
      })
    }

    addCopyDetailAction(orderData, actions)
    addResolveIssuesAction(orderData, actions)
    addAdminActions(order, actions)

    if (extraOptions.length > 0) {
      actions.push(...extraOptions)
    }

    return actions
  }, [
    order,
    t,
    addCopyDetailAction,
    addResolveIssuesAction,
    addAdminActions,
    openEditOrder,
    onOrderHistory,
    onEditPostion,
    onAddHold,
    isContainerHistoryTurnedOn,
    extraOptions,
    navigate,
  ])

  return (
    <>
      {!!actions?.length && (
        <OptionsButton options={actions} position='right' testId='order-options-button' size={size} />
      )}
      <AddHoldDialog {...dialogProps} />
    </>
  )
}
